import { Col, Row, Tabs } from "antd";
import React, { useEffect, useState } from "react";
import { InterviewExperienceAPI } from "../api/InterviewExperienceAPI";
import { ImageComponent } from "../components/common/Image.component";
import { Loader } from "../components/common/Loader.component";
import { SearchTokenHelper } from "../helpers/Search.helper";
import { InterviewExperiencesListItem } from "../interivew-experience/InterviewExperiencesList";
import { Link } from "../links/Link";
import { ListItemLayoutCard } from "../list/ListItem.Layout";
import { ListItemProps, ListV2 } from "../list/ListV2";
import { AttachmentTargetType } from "../model/AttachmentTargetType";
import { InterviewExperienceResponse } from "../model/InterviewExperience";
import { ReactMatch } from "../model/ReactMatchObj";
import { PageProps } from "../model/views/PageProps";
import { QuestionDl } from "../question/Question.Dl";
import { Question } from "../question/Question.Model";
import { QuestionsAPI } from "../question/QuestionsAPI";
import { QuestionsFilterView } from "../question/QuestionsFilterView";
import { QuestionsListItem } from "../question/QuestionsList";
import { ViewsManager } from "../reactions/Views.Manager";
import { SocialTypeId } from "../social/Social.model";
import { TopicType } from "../topic/Topic.model";
import { Company } from "./Company";
import { CompanyHelper } from "./Company.Helper";
import { CompanyImage } from "./Company.Image";
import { CompanyAPI } from "./CompanyAPI";

interface CompanyViewPagePathParams {
  companyId: string;
  slug: string;
}

interface CompanyViewPageProps extends PageProps {
  match: ReactMatch<CompanyViewPagePathParams>;
}

enum CompanyPageTab {
  OVERVIEW = "OVERVIEW",
  INTERVIEW_EXPERIENCES = "INTERVIEW_EXPERIENCES",
  QUESTIONS = "QUESTIONS",
}

export function CompanyViewPage(props: CompanyViewPageProps) {
  const { companyId } = props.match.params;
  const [company, setCompany] = useState<Company>();
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<CompanyPageTab>(CompanyPageTab.OVERVIEW);
  const website = company?.socialLinks?.find((sl) => sl.type.id === SocialTypeId.WEBSITE)?.url;


  useEffect(() => {
    setLoading(true);
    CompanyAPI.getCompany(companyId)
      .executeV2()
      .then((company: Company) => {
        setCompany(company);
      })
      .finally(() => {
        setLoading(false);
      });
    ViewsManager.log({ targetType: AttachmentTargetType.COMPANY, targetId: companyId });
  }, [companyId]);

  const companyToken = SearchTokenHelper.getTopicToken({
    topics: [{ topicId: { type: TopicType.COMPANY, id: companyId }, topicType: TopicType.COMPANY, dl: "Company" }],
  });

  const renderOverview = () => (
    <Row>
      <Col md={16}>
        <div className="mb-4">
          <h2 className="h4 mb-3">About</h2>
          <p className="text-secondary" style={{ whiteSpace: "pre-wrap" }}>
            {company?.dd || "No description available."}
          </p>
        </div>

        {company?.specialties && (
          <div className="mb-4">
            <h2 className="h4 mb-3">Specialties</h2>
            <div className="d-flex flex-wrap gap-2">
              {company.specialties.split(",").map((s, i) => (
                <span key={i} className="badge bg-light text-dark border p-2">
                  {s.trim()}
                </span>
              ))}
            </div>
          </div>
        )}
      </Col>

      <Col md={8}>
        {(website || company?.industry || company?.companySize || company?.headquarters || company?.foundedYear) && (
          <div className="card border-0 shadow-sm p-4 bg-light">
            <h3 className="h5 mb-4">Company Details</h3>
            <ul className="list-unstyled mb-0">
              {website && (
                <li className="mb-3">
                  <div className="text-muted small">Website</div>
                  <a href={website} target="_blank" rel="noopener noreferrer" className="text-primary text-decoration-none">
                    {website.replace(/^https?:\/\/(www\.)?/, "")}
                  </a>
                </li>
              )}

              {company?.industry && (
                <li className="mb-3">
                  <div className="text-muted small">Industry</div>
                  <div className="fw-medium">{company.industry}</div>
                </li>
              )}
              {company?.companySize && (
                <li className="mb-3">
                  <div className="text-muted small">Company size</div>
                  <div className="fw-medium">{company.companySize}</div>
                </li>
              )}
              {company?.headquarters && (
                <li className="mb-3">
                  <div className="text-muted small">Headquarters</div>
                  <div className="fw-medium">{company.headquarters}</div>
                </li>
              )}
              {company?.foundedYear && (
                <li className="mb-0">
                  <div className="text-muted small">Founded</div>
                  <div className="fw-medium">{company.foundedYear}</div>
                </li>
              )}
            </ul>
          </div>
        )}
      </Col>
    </Row>
  );

  const renderInterviewExperiences = () => (
    <div className="mt-4">
      <ListV2
        paginationPosition="bottom"
        user={props.user?.basicDetails}
        gutter={[15, 15]}
        onFetch={(searchRequest) => {
          const companySearchToken = SearchTokenHelper.getCompanyToken([{ id: companyId, dl: company?.dl ?? "" } as Company]);
          const request = {
            ...searchRequest,
            searchTokens: [...(searchRequest?.searchTokens ?? []), companySearchToken],
          };
          return InterviewExperienceAPI.search().executeV2(request);
        }}
        itemNode={(itemProps: ListItemProps) => {
          const item = itemProps.item as InterviewExperienceResponse;
          return (
            <InterviewExperiencesListItem
              interviewExperience={item}
              dl={<span className="h5">{item.dl}</span>}
              liu={props.user}
            />
          );
        }}
        emptyListContent="No interview experiences found for this company."
        ItemLayoutComponent={ListItemLayoutCard}
      />
    </div>
  );

  const renderQuestions = () => (
    <div className="mt-4">
      <ListV2
        paginationPosition="bottom"
        user={props.user?.basicDetails}
        SearchComponent={QuestionsFilterView}
        initialStartingFilterToken={SearchTokenHelper.getAndToken([companyToken])}
        gutter={[10, 10]}
        onFetch={(searchRequest) => {
          return QuestionsAPI.search().executeV2(searchRequest ?? {});
        }}
        itemNode={(itemProps: ListItemProps) => {
          const question = itemProps.item as Question;
          return (
            <QuestionsListItem
              question={question}
              dl={
                <span className="h5">
                  <QuestionDl ques={question} />
                </span>
              }
              liu={props.user}
            />
          );
        }}
        emptyListContent="No questions found for this company."
        ItemLayoutComponent={ListItemLayoutCard}
      />
    </div>
  );

  return (
    <div className="CompanyHomepage bg-white min-vh-100">
      {loading && <Loader />}
      {!loading && company && (
        <>
          {/* Hero Section */}
          <div className="position-relative bg-light mb-4" style={{ height: "200px", zIndex: 1 }}>
            {company.banner && company.banner.locationType && company.banner.data && (
              <img src={company.banner.data} alt="Company banner" className="w-100 h-100" style={{ objectFit: "cover" }} />
            )}
            <div className="container position-relative">
              <div className="position-absolute translate-middle-y" style={{ bottom: company.banner && company.banner.locationType && company.banner.data ? "-100px" : "-300px", left: "15px", zIndex: 10 }}>
                <div className="p-2 bg-white rounded shadow-sm border">
                  <CompanyImage company={company} height={100} width={100} />
                </div>
              </div>
            </div>
          </div>

          <div className="container" style={{ marginTop: "60px", zIndex: 20000 }}>
            <div className="d-flex justify-content-between align-items-start mb-4">
              <div>
                <h1 className="h2 mb-1">{company.desDl || company.dl}</h1>
                <p className="text-muted h5 fw-normal mb-2">{company.tl}</p>
                <div className="d-flex align-items-center gap-3 small text-muted">
                  {company.industry && <span>{company.industry}</span>}
                  {company.headquarters && <span>• {company.headquarters}</span>}
                  {/* {website && (
                    <a href={website} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-primary">
                      <i className="fas fa-globe me-1"></i> Website
                    </a>
                  )} */}
                </div>
                {company.socialLinks && company.socialLinks.length > 0 && (
                  <div className="hstack gap-2 overflow-auto mt-3">
                    {company.socialLinks.map((sl, index) => (
                      <Link target="_blank" href={sl.url} className="text-dark fs-4 mr-1" key={index}>
                        <div className="border px-3 py-2 border-warning rounded" title={sl.type.dl}>
                          <ImageComponent image={sl.type.dp} />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              {props.user?.basicDetails.isAdmin && (
                <Link href={CompanyHelper.getEditUrl(company)} className="btn btn-outline-secondary btn-sm">
                  Edit metadata
                </Link>
              )}
            </div>

            {/* Tabs */}
            <Tabs
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key as CompanyPageTab)}
              items={[
                {
                  key: CompanyPageTab.OVERVIEW,
                  label: "Overview",
                  children: renderOverview(),
                },
                {
                  key: CompanyPageTab.INTERVIEW_EXPERIENCES,
                  label: "Interview Experiences",
                  children: renderInterviewExperiences(),
                },
                {
                  key: CompanyPageTab.QUESTIONS,
                  label: "Questions",
                  children: renderQuestions(),
                },
              ]}
              className="company-tabs"
            />
          </div>
        </>
      )}
    </div>
  );
}
